import { useCallback, useEffect, useState } from 'react'

import { useLazyGetEmailTypeByIdQuery } from '../../../store/integrations/emailTypes'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { EmailType, EmailTypeKind } from '../../../models/EmailType'
import { InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'
import {
  InputParametersHierarchyDefault,
  useCreateNodeMutation,
  useLazyGetEffectiveNodeQuery,
  useLazyGetInputParametersHierarchyDefaultQuery,
} from '../../../store/ai/inputParametersHierarchy'
import { InputParametersHierarchyLevel } from '../../../models/InputParametersHierarchy'

export const EMAIL_TEMPLATE_IP_HIERARCHY_TYPE_MAPPING = {
  [EmailTypeKind.REPLY]: InputParameterHierarchyType.EMAIL_RESPONSE_TEMPLATE,
  [EmailTypeKind.NEW]: InputParameterHierarchyType.NEW_EMAIL_TEMPLATE,
}

export const useEmailType = (emailTypeId: string) => {
  const accountId = useAppSelector(selectAccountId)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [emailType, setEmailType] = useState<EmailType | null>(null)

  const [triggerGetEmailTypeById] = useLazyGetEmailTypeByIdQuery()

  const fetchEmailType = useCallback(async () => {
    if (!emailTypeId) {
      return null
    }
    setIsLoading(true)
    try {
      const { data: emailType } = await triggerGetEmailTypeById({ accountId, emailTypeId }).unwrap()
      if (!emailType) {
        return null
      }
      setEmailType(emailType)
      return emailType
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
    return null
  }, [accountId, emailTypeId, triggerGetEmailTypeById])

  useEffect(() => {
    fetchEmailType()
  }, [emailTypeId, fetchEmailType])

  return { emailType, isLoading, fetchEmailType }
}

interface UseCopyEmailTemplateProps {
  emailType: EmailType | null
}

export const useCopyEmailTemplate = ({ emailType }: UseCopyEmailTemplateProps) => {
  const accountId = useAppSelector(selectAccountId)

  const [getInputParameterHierarchyNode] = useLazyGetEffectiveNodeQuery()
  const [createInputParametersHierarchyNode] = useCreateNodeMutation()
  const [triggerGetIpHierarchyDefault] = useLazyGetInputParametersHierarchyDefaultQuery()

  const [ipHierarchyDefault, setIpHierarchyDefault] = useState<InputParametersHierarchyDefault | null>(null)

  useEffect(() => {
    const fetchIpHierarchyDefault = async () => {
      if (!emailType) {
        return
      }
      const { kind, systemEmailTypeId } = emailType
      try {
        const { data: ipHierarchyDefaultResponse } = await triggerGetIpHierarchyDefault({
          accountId,
          type: EMAIL_TEMPLATE_IP_HIERARCHY_TYPE_MAPPING[kind],
          typeId: systemEmailTypeId ?? 'default-email',
        })
        if (!ipHierarchyDefaultResponse) {
          setIpHierarchyDefault(null)
          return
        }
        setIpHierarchyDefault(ipHierarchyDefaultResponse)
      } catch (e) {
        console.error(e)
      }
    }
    fetchIpHierarchyDefault()
  }, [accountId, emailType, triggerGetIpHierarchyDefault])

  const createCopiedInputParameters = useCallback(
    async (type: InputParameterHierarchyType, typeId: string, newEmailTypeId: string, templateId: string) => {
      try {
        await createInputParametersHierarchyNode({
          accountId,
          type,
          typeId,
          node: {
            hierarchyId: { level: InputParametersHierarchyLevel.EMAIL_TYPE_GLOBAL, emailTypeId: newEmailTypeId },
            templateId,
          },
        }).unwrap()
      } catch (e) {
        console.error(e)
      }
    },
    [accountId, createInputParametersHierarchyNode],
  )

  const copyTemplate = useCallback(
    async (type: InputParameterHierarchyType, typeId: string, newEmailTypeId: string) => {
      if (!emailType) {
        return
      }

      const fromSystemEmailType = !!emailType.systemEmailTypeId

      if (fromSystemEmailType && ipHierarchyDefault) {
        await createCopiedInputParameters(type, typeId, newEmailTypeId, ipHierarchyDefault.templateId)
        return
      }

      const { id: emailTypeId } = emailType

      const template = await getInputParameterHierarchyNode({
        accountId,
        type,
        typeId,
        context: { effective: false, level: InputParametersHierarchyLevel.EMAIL_TYPE_GLOBAL, emailTypeId, contextType: 'email_type' },
      }).unwrap()

      if (!template?.templateId) {
        return
      }

      await createCopiedInputParameters(type, typeId, newEmailTypeId, template.templateId)
    },
    [accountId, emailType, createCopiedInputParameters, getInputParameterHierarchyNode, ipHierarchyDefault],
  )

  const copyTemplates = useCallback(
    async (newEmailTypeId: string) => {
      if (!emailType) {
        return
      }
      const templatesToCopy = [{ type: EMAIL_TEMPLATE_IP_HIERARCHY_TYPE_MAPPING[emailType.kind], typeId: 'email-template' }]
      await Promise.all(templatesToCopy.map(({ type, typeId }) => copyTemplate(type, typeId, newEmailTypeId)))
    },
    [emailType, copyTemplate],
  )

  return { copyTemplates }
}
